import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { type FieldError, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Textfield } from '@bi-digital/react';
import { createUser } from '../../api';
import { Result } from '../../components/result/result';
import { type AuthCatalogUserForm, authCatalogUserSchema, type User } from '../../types';

import styles from './verification.module.css';

export interface VerificationProps {
    validationErrorText: string;
    title: string;
    buttonText: string;
    verifiedNewUserText: string;
    verifiedNewUserText2: string;
    errorText: string;
    signUpUrl: string;
    portalUrl: string;
    linkText: string;
    user?: User;
}

export const Verification = ({
    validationErrorText,
    title,
    buttonText,
    verifiedNewUserText,
    verifiedNewUserText2,
    errorText,
    signUpUrl,
    portalUrl,
    linkText,
    user,
}: VerificationProps) => {
    const [wizardStep, setWizardStep] = useState<'form' | 'confirm' | 'error'>('form');

    const { t } = useTranslation(['iam', 'common']);
    const { register, handleSubmit, formState } = useForm<AuthCatalogUserForm>({
        resolver: zodResolver(authCatalogUserSchema),
    });

    const formatValidationMessage = (error?: FieldError) => {
        if (!error || !error.message) return '';
        if (error.type === 'invalid_type') return t('common:required');
        return `${t('iam:reqTitle')} ${t(error.message)}`;
    };

    const handleCreateUser = handleSubmit(async ({ pwd1 }) => {
        if (typeof user === 'undefined') return;
        const newUser: User = {
            ...user,
            password: pwd1,
        };
        try {
            await createUser(newUser);
            setWizardStep('confirm');
        } catch {
            setWizardStep('error');
        }
    });

    if (wizardStep === 'form') {
        return (
            <>
                {!user ? (
                    <Result message={validationErrorText} linkText={t('iam:TryAgainLink')} link={signUpUrl} />
                ) : (
                    <div className={styles.verification}>
                        <h1>{title}</h1>
                        <Textfield label={t('iam:email')} defaultValue={user.email} name='email' readOnly />
                        <div>
                            <p>{t('iam:reqTitle')}</p>
                            <ul>
                                <li>{t('iam:newPasswordReq1')}</li>
                                <li>{t('iam:newPasswordReq2a')}</li>
                                <li>{t('iam:newPasswordReq2b')}</li>
                            </ul>
                        </div>
                        <form className={styles.form} onSubmit={handleCreateUser}>
                            <Textfield
                                label={t('iam:password1')}
                                type='password'
                                error={formatValidationMessage(formState.errors.pwd1)}
                                required
                                {...register('pwd1')}
                            />
                            <Textfield
                                label={t('iam:password2')}
                                type='password'
                                required
                                error={formatValidationMessage(formState.errors.pwd2)}
                                {...register('pwd2')}
                            />
                            <Button
                                disabled={formState.isSubmitting}
                                submitting={formState.isSubmitting}
                                fullwidth
                                type='submit'
                            >
                                {buttonText}
                            </Button>
                        </form>
                    </div>
                )}
            </>
        );
    } else if (wizardStep === 'confirm')
        return (
            <Result
                message={verifiedNewUserText}
                message2={verifiedNewUserText2}
                link={portalUrl}
                linkText={linkText}
                success
            />
        );
    return <Result message={errorText} />;
};
